import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import instance from "../instance";
import CustomerTable from "../components/Customers/CustomerTable";
import CustomerFilters from "../components/Customers/CustomerFilters";
import CustomerSearch from "../components/Customers/CustomerSearch";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { formatDate } from "./Customers/utils";
import {
  fetchPayPreview,
  buildPayDialogMessage,
  buildRenewalClipboardText,
  datesFromPayResponse,
  liveExpiryFromPreview,
  buildPaySuccessToast,
  buildSmsRenewalText,
  buildInfoCopyCardText,
  buildNoticeText,
  buildLateNoticeText,
  buildLateText,
} from "./Customers/payIntentHelpers";
import "./Customer.css";

const Customers = ({ handleLogout }) => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");
  const [isTotalVisible, setIsTotalVisible] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [dateFilter, setDateFilter] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [paymentNoticeCount, setPaymentNoticeCount] = useState(0);
  const [arrearsCount, setArrearsCount] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);
  const [speechTotal, setSpeechTotal] = useState(0);

  const [persistentPaymentNoticeRows, setPersistentPaymentNoticeRows] =
    useState([]);
  const [filters, setFilters] = useState({
    online: false,
    offline: false,
    overdue: false,
    paymentNotice: false,
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [datalistData, setDatalistData] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.date) {
      setDateFilter(new Date(location.state.date));
      window.history.replaceState({}, document.title);
    } else if (
      location.state &&
      location.state.startDate &&
      location.state.endDate
    ) {
      setDateFilter({
        start: new Date(location.state.startDate),
        end: new Date(location.state.endDate),
      });
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setUserRole(decodedToken.role);
    }
  }, []);

  
  useEffect(() => {
    fetchCustomerData();
  }, []);

  useEffect(() => {
    // جلب Speech Total فقط للمدير بعد تعيين userRole
    if (userRole === "admin") {
      fetchSpeechTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSpeechTotal is recreated each render; adding it would refetch on every paint
  }, [userRole]);

  useEffect(() => {
    updateDatalistData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateDatalistData is recreated each render; adding it would rebuild the datalist on every paint
  }, [customers]);

  useEffect(() => {
    calculateTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- calculateTotals is recreated each render; adding it would loop
  }, [customers, filters, searchTerm, excludeTerm, sortConfig, dateFilter]);

  const updateDatalistData = () => {
    const excludeSet = new Set();
    customers.forEach((customer) => {
      [
        "Code",
        "Name",
        "Governorate",
        "City",
        "NameOwner",
        "PaymentDate",
        "PriceType",
        "ServerStatus",
        "Status",
        "VersionBack",
        "VersionFront",
      ].forEach((key) => {
        if (customer[key]) {
          const value =
            key === "PaymentDate"
              ? new Date(customer[key]).toLocaleDateString()
              : customer[key].toString().toLowerCase();
          excludeSet.add(value);
        }
      });
    });
    setDatalistData(Array.from(excludeSet).sort());
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleExcludeSearch = (e) => {
    setExcludeTerm(e.target.value.toLowerCase());
  };

  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
    setFilters({
      online: false,
      offline: false,
      overdue: false,
      paymentNotice: false,
    });
    setSortConfig({ key: null, direction: "ascending" });
    setDateFilter(null);
  };

  const toggleFilter = (filterName) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev, [filterName]: !prev[filterName] };
      if (!updatedFilters.paymentNotice && !updatedFilters.overdue) {
        setSortConfig({ key: null, direction: "ascending" });
      } else if (filterName === "paymentNotice" && !prev[filterName]) {
        setSortConfig({ key: "PaymentDate", direction: "ascending" });
      } else if (filterName === "overdue" && !prev[filterName]) {
        setSortConfig({ key: "PaymentDate", direction: "descending" });
      }
      return updatedFilters;
    });
  };
  const filterCustomers = (customer) => {
    const { online, offline, overdue, paymentNotice } = filters;

    // Check if this customer should persist in paymentNotice filter
    const isPersistentPaymentNotice = persistentPaymentNoticeRows.includes(
      customer.Code
    );

    if (online && customer.Status !== "Online") return false;
    if (offline && customer.Status !== "Offline") return false;

    const customerValues = [
      "Code",
      "Name",
      "Governorate",
      "City",
      "NameOwner",
      "PaymentDate",
      "PriceType",
      "ServerStatus",
      "Status",
      "VersionBack",
      "VersionFront",
    ].map((key) =>
      customer[key]
        ? key === "PaymentDate"
          ? new Date(customer[key]).toLocaleDateString()
          : customer[key].toString().toLowerCase()
        : ""
    );

    if (excludeTerm && customerValues.some((value) => value === excludeTerm))
      return false;
    if (
      searchTerm &&
      !customerValues.some((value) => value.includes(searchTerm))
    )
      return false;

    if (overdue) {
      const today = new Date();
      const paymentDate = new Date(customer.PaymentDate);
      if (!customer.PaymentDate || isNaN(paymentDate.getTime())) return false;
      if (
        paymentDate > today ||
        paymentDate.toDateString() === today.toDateString()
      )
        return false;
    }

    if (paymentNotice && !isPersistentPaymentNotice) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const twoDaysFromNow = new Date(
        today.getTime() + 2 * 24 * 60 * 60 * 1000
      );
      twoDaysFromNow.setHours(23, 59, 59, 999);
      const paymentDate = new Date(customer.PaymentDate);
      if (!customer.PaymentDate || isNaN(paymentDate.getTime())) return false;
      if (paymentDate <= today || paymentDate >= twoDaysFromNow) return false;

      // If this customer passes the paymentNotice filter, add it to persistentPaymentNoticeRows
      setPersistentPaymentNoticeRows((prev) => [...prev, customer.Code]);
    }

    if (dateFilter) {
      const customerPaymentDate = new Date(customer.PaymentDate);
      if (dateFilter instanceof Date) {
        if (customerPaymentDate.toDateString() !== dateFilter.toDateString()) {
          return false;
        }
      } else if (dateFilter.start && dateFilter.end) {
        if (
          customerPaymentDate < dateFilter.start ||
          customerPaymentDate > dateFilter.end
        ) {
          return false;
        }
      }
    }

    return true;
  };
  useEffect(() => {
    calculateTotals();
    calculatePaymentNoticeCount();
    calculateArrearsCount();
    calculateOnlineOfflineCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- count helpers are recreated each render; adding them would loop
  }, [customers, filters, searchTerm, excludeTerm, sortConfig, dateFilter]);

  const calculatePaymentNoticeCount = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twoDaysFromNow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
    twoDaysFromNow.setHours(23, 59, 59, 999);

    const count = customers.filter((customer) => {
      const paymentDate = new Date(customer.PaymentDate);
      return paymentDate > today && paymentDate <= twoDaysFromNow;
    }).length;

    setPaymentNoticeCount(count);
  };

  const calculateArrearsCount = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // تعيين الوقت إلى بداية اليوم

    const count = customers.filter((customer) => {
      if (!customer.PaymentDate) return false; // تجاهل التواريخ الفارغة

      const paymentDate = new Date(customer.PaymentDate);
      if (isNaN(paymentDate.getTime())) return false; // تجاهل التواريخ غير الصالحة

      return paymentDate < today;
    }).length;

    setArrearsCount(count);
  };

  const calculateOnlineOfflineCounts = () => {
    const onlineCount = customers.filter(
      (customer) => customer.Status === "Online"
    ).length;
    const offlineCount = customers.filter(
      (customer) => customer.Status === "Offline"
    ).length;
    setOnlineCount(onlineCount);
    setOfflineCount(offlineCount);
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    // ترتيب الافتراضي بالتاريخ
    if (key === "PaymentDate") {
      const sortedCustomers = [...customers].sort((a, b) => {
        const dateA = a.PaymentDate ? new Date(a.PaymentDate) : null;
        const dateB = b.PaymentDate ? new Date(b.PaymentDate) : null;

        if (dateA === null && dateB === null) return 0;
        if (dateA === null) return 1; // تواريخ فارغة في الأسفل
        if (dateB === null) return -1;

        return direction === "ascending" ? dateA - dateB : dateB - dateA;
      });
      setCustomers(sortedCustomers);
    }
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });

  const handleRegisterClick = () => {
    navigate("/register");
  };

  const handleUsersClick = () => {
    navigate("/users");
  };

  const toggleTotalVisibility = () => {
    setIsTotalVisible(!isTotalVisible);
  };

  const fetchCustomerData = async () => {
    try {
      // جلب بيانات العملاء والشركاء في نفس الوقت
      const [customerResponse, partnerResponse] = await Promise.all([
        instance.get(`merged`),
        instance.get(`partner/login`)
      ]);

      if (customerResponse.status !== 200) {
        throw new Error("Failed to fetch customer data.");
      }

      const customerData = customerResponse.data;
      const partnerData = partnerResponse.data;

      // إنشاء خريطة للشركاء باستخدام NameDatabase كـ key (مع تجاهل الحروف الكبيرة والصغيرة)
      const partnerMap = new Map();
      partnerData.forEach(partner => {
        partnerMap.set(partner.NameDatabase.toLowerCase(), partner.LastLogin);
      });

      // إضافة debugging لمعرفة البيانات
      console.log('Partner Data:', partnerData);
      console.log('Partner Map:', Object.fromEntries(partnerMap));

      const sortedCustomers = customerData
        .sort((a, b) => {
          const dateA = a.PaymentDate ? new Date(a.PaymentDate) : null;
          const dateB = b.PaymentDate ? new Date(b.PaymentDate) : null;

          if (dateA === null && dateB === null) return 0;
          if (dateA === null) return 1;
          if (dateB === null) return -1;

          return dateA - dateB;
        })
        .map((customer) => {
          const loginStatus = partnerMap.get(customer.Name.toLowerCase()) || null;
          
          // إضافة debugging لكل عميل
          console.log('Customer Mapping:', {
            customerName: customer.Name,
            customerNameLower: customer.Name.toLowerCase(),
            loginStatus: loginStatus,
            hasMatch: partnerMap.has(customer.Name.toLowerCase())
          });
          
          return {
            ...customer,
            phone: customer.Phone || "",
            LoginStatus: loginStatus, // ربط Name مع NameDatabase (مع تجاهل الحروف الكبيرة والصغيرة)
          };
        });

      setCustomers(sortedCustomers);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  const fetchSpeechTotal = async () => {
    // جلب Speech Total فقط للمدير
    if (userRole !== "admin") {
      return;
    }
    try {
      const response = await instance.get(`speech-subscriptions/`);
      if (response.status === 200 && response.data?.data) {
        const speechSubscriptions = response.data.data;
        const total = speechSubscriptions.reduce(
          (sum, subscription) => sum + (parseFloat(subscription.Price) || 0),
          0
        );
        setSpeechTotal(total);
      }
    } catch (error) {
      console.error("Error fetching speech total:", error);
      setSpeechTotal(0);
    }
  };

  const handleRowClick = (code, name) => {
    if (userRole === "admin") {
      localStorage.setItem("customerName", name);
      navigate(`/customer?code=${code}`);
    }
  };

  function capitalizeName(name) {
    // Capitalize the first letter of the name
    name = name.charAt(0).toUpperCase() + name.slice(1);

    // Regex to find "El" or "Al" and capitalize the next character
    const regex = /\b(El|Al)([a-z])/g;
    name = name.replace(regex, function (match, p1, p2) {
      return p1 + p2.toUpperCase();
    });

    return name;
  }


  const handlePay = async (code, paymentDate, name, priceType = "", phone) => {
    const index = customers.findIndex((customer) => customer.Code === code);

    let preview;
    try {
      preview = await fetchPayPreview(instance, code, 1);
    } catch (previewError) {
      console.error("Error loading live payment preview:", previewError);
      toast.error("Could not load the live expiry. Refresh and try again.");
      return;
    }

    const dialogMessage = buildPayDialogMessage({
      name,
      preview,
      gridPaymentDate: paymentDate,
      includeAdminNotes: userRole !== "supervisor",
    });

    const copyRenewalFromServer = (paymentExpiration) => {
      const text = buildRenewalClipboardText({
        name,
        priceType,
        paymentExpiration,
        capitalizeName,
      });
      try {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard
            .writeText(text)
            .then(() => toast.success("Message copied to clipboard automatically"))
            .catch(() => {
              const textarea = document.createElement("textarea");
              textarea.value = text;
              textarea.setAttribute("readonly", "");
              textarea.style.position = "absolute";
              textarea.style.left = "-9999px";
              document.body.appendChild(textarea);
              textarea.select();
              try {
                document.execCommand("copy");
                toast.success("Message copied to clipboard automatically");
              } catch (err) {
                toast.error("Failed to copy message");
              }
              document.body.removeChild(textarea);
            });
        }
      } catch (err) {
        console.error("Clipboard copy failed:", err);
      }
    };

    const updateSpeechFromServerDates = async ({
      currentPaymentDate,
      paymentExpiration,
      orderId,
      addSpeechAmount,
    }) => {
      const speechResponse = await instance.get(`speech-subscriptions/by-code/${code}`);
      if (!(speechResponse.status === 200 && speechResponse.data?.data?.IsActive)) {
        return false;
      }
      const speechPrice = parseFloat(speechResponse.data.data.Price || "0");
      await instance.put(`speech-subscriptions/by-code/${code}`, {
        StartDate: currentPaymentDate,
        EndDate: paymentExpiration,
        IsActive: true,
        Price: speechResponse.data.data.Price || "1000",
      });
      if (addSpeechAmount && orderId && speechPrice > 0) {
        await instance.put(`payall/pay/${code}/update-speech-amount`, {
          orderId,
          speechAmount: speechPrice,
        });
      }
      return true;
    };

    // إذا كان المستخدم مشرف، اعرض خيارات محدودة
    if (userRole === "supervisor") {
      confirmAlert({
        customUI: ({ onClose }) => (
          <div className="react-confirm-alert-body">
            <h1>Confirm to payment</h1>
            <p style={{ whiteSpace: "pre-wrap" }}>{dialogMessage}</p>
            <div className="react-confirm-alert-button-group">
              <button
                onClick={() => {
                  onClose();
                  (async () => {
                    try {
                      const paymentResponse = await instance.put(`payall/pay/${code}`, {
                        NumberMonths: 1,
                      });
                      if (paymentResponse.status === 200) {
                        const paid = datesFromPayResponse(paymentResponse);
                        toast.success(
                          buildPaySuccessToast({
                            writtenExpiry: paid.paymentExpiration,
                            previewExpiry: preview.previewPaymentDate,
                            orderId: paid.orderId,
                          })
                        );
                        try {
                          const speechResponse = await instance.get(
                            `speech-subscriptions/by-code/${code}`
                          );
                          if (
                            speechResponse.status === 200 &&
                            speechResponse.data?.data?.IsActive
                          ) {
                            confirmAlert({
                              title: "Update Speech Subscription?",
                              message:
                                "Do you want to update the Speech Subscription and add speech amount to the order as well?",
                              buttons: [
                                {
                                  label: "Yes",
                                  onClick: async () => {
                                    try {
                                      await updateSpeechFromServerDates({
                                        currentPaymentDate: paid.currentPaymentDate,
                                        paymentExpiration: paid.paymentExpiration,
                                        orderId: paid.orderId,
                                        addSpeechAmount: true,
                                      });
                                      toast.success(
                                        "Payment and Speech Subscription updated successfully"
                                      );
                                    } catch (error) {
                                      console.error(
                                        "Error updating Speech Subscription:",
                                        error
                                      );
                                      toast.error(
                                        "Payment updated, but failed to update Speech Subscription"
                                      );
                                    }
                                  },
                                },
                                {
                                  label: "No",
                                  onClick: () => {
                                    toast.success(
                                      buildPaySuccessToast({
                                        writtenExpiry: paid.paymentExpiration,
                                        previewExpiry: preview.previewPaymentDate,
                                        orderId: paid.orderId,
                                      })
                                    );
                                  },
                                },
                              ],
                            });
                          } else {
                            toast.success(
                              buildPaySuccessToast({
                                writtenExpiry: paid.paymentExpiration,
                                previewExpiry: preview.previewPaymentDate,
                                orderId: paid.orderId,
                              })
                            );
                          }
                        } catch (speechError) {
                          console.log(
                            "ℹ️ No active speech subscription to update or error:",
                            speechError.message
                          );
                          toast.success(
                            buildPaySuccessToast({
                              writtenExpiry: paid.paymentExpiration,
                              previewExpiry: preview.previewPaymentDate,
                              orderId: paid.orderId,
                            })
                          );
                        }
                        fetchCustomerData();
                        copyRenewalFromServer(paid.paymentExpiration);
                      } else {
                        toast.error("❌ Error updating payment and subscription");
                      }
                    } catch (error) {
                      console.error("Error updating SubscribeUntill:", error);
                      toast.error(`❌ Error updating Subscribe ${name}`);
                    }
                  })();
                }}
              >
                Both
              </button>
              <button onClick={onClose}>Cancel</button>
            </div>
          </div>
        ),
      });
      return;
    }

    confirmAlert({
      title: "Confirm to payment",
      message: (
        <span style={{ whiteSpace: "pre-wrap" }}>{dialogMessage}</span>
      ),
      buttons: [
        {
          label: "Payment",
          onClick: async () => {
            try {
              const response = await instance.put(`payment/pay/${code}`, {
                NumberMonths: 1,
              });

              if (response.status === 200) {
                const paid = datesFromPayResponse(response);
                setCustomers((prevCustomers) => {
                  const updatedCustomers = [...prevCustomers];
                  if (index !== -1) {
                    updatedCustomers[index] = {
                      ...updatedCustomers[index],
                      PaymentDate: paid.paymentExpiration,
                    };
                  }
                  return updatedCustomers;
                });

                try {
                  const speechResponse = await instance.get(`speech-subscriptions/by-code/${code}`);
                  if (speechResponse.status === 200 && speechResponse.data?.data?.IsActive) {
                    confirmAlert({
                      title: "Update Speech Subscription?",
                      message: "Do you want to update the Speech Subscription dates as well?",
                      buttons: [
                        {
                          label: "Yes",
                          onClick: async () => {
                            try {
                              await updateSpeechFromServerDates({
                                currentPaymentDate: paid.currentPaymentDate,
                                paymentExpiration: paid.paymentExpiration,
                                addSpeechAmount: false,
                              });
                              toast.success("Payment and Speech Subscription updated successfully");
                            } catch (error) {
                              console.error("Error updating Speech Subscription:", error);
                              toast.error("Payment updated, but failed to update Speech Subscription");
                            }
                          },
                        },
                        {
                          label: "No",
                          onClick: () => {
                            toast.success(
                              buildPaySuccessToast({
                                writtenExpiry: paid.paymentExpiration,
                                previewExpiry: preview.previewPaymentDate,
                              })
                            );
                          },
                        },
                      ],
                    });
                  } else {
                    toast.success(
                      buildPaySuccessToast({
                        writtenExpiry: paid.paymentExpiration,
                        previewExpiry: preview.previewPaymentDate,
                      })
                    );
                  }
                } catch (speechError) {
                  console.log("ℹ️ No active speech subscription to update or error:", speechError.message);
                  toast.success(
                    buildPaySuccessToast({
                      writtenExpiry: paid.paymentExpiration,
                      previewExpiry: preview.previewPaymentDate,
                    })
                  );
                }
              } else {
                toast.error("Error updating payment date");
              }
            } catch (error) {
              console.error("Error updating payment date:", error);
              toast.error("Error updating payment date");
            }
          },
        },
        {
          label: "Subscr",
          onClick: async () => {
            try {
              const response = await instance.put(`payall/sync-tenant/${code}`, {});
              if (response.status === 200) {
                const live = datesFromPayResponse(response);
                toast.success(
                  buildPaySuccessToast({
                    writtenExpiry: live.currentPaymentDate || live.subscribeUntill,
                    previewExpiry: preview.currentPaymentDate,
                    actionLabel: "Tenant set to catalog expiry",
                  })
                );
                fetchCustomerData();
              } else {
                toast.error(`Error updating Subscribe ${name}`);
              }
            } catch (error) {
              console.error("Error updating SubscribeUntill:", error);
              toast.error(`Error updating Subscribe ${name}`);
            }
          },
        }
        ,
        {
          label: "Both",
          onClick: async () => {
            try {
              const paymentResponse = await instance.put(`payall/pay/${code}`, {
                NumberMonths: 1,
              });
        
              if (paymentResponse.status === 200) {
                const paid = datesFromPayResponse(paymentResponse);
                toast.success(
                  buildPaySuccessToast({
                    writtenExpiry: paid.paymentExpiration,
                    previewExpiry: preview.previewPaymentDate,
                    orderId: paid.orderId,
                  })
                );
                
                try {
                  const speechResponse = await instance.get(`speech-subscriptions/by-code/${code}`);
                  
                  if (speechResponse.status === 200 && speechResponse.data?.data?.IsActive) {
                    confirmAlert({
                      title: "Update Speech Subscription?",
                      message: "Do you want to update the Speech Subscription dates as well?",
                      buttons: [
                        {
                          label: "Yes",
                          onClick: async () => {
                            try {
                              await updateSpeechFromServerDates({
                                currentPaymentDate: paid.currentPaymentDate,
                                paymentExpiration: paid.paymentExpiration,
                                orderId: paid.orderId,
                                addSpeechAmount: true,
                              });
                              toast.success("Payment and Speech Subscription updated successfully");
                            } catch (error) {
                              console.error("Error updating Speech Subscription:", error);
                              toast.error("Payment updated, but failed to update Speech Subscription");
                            }
                          },
                        },
                        {
                          label: "No",
                          onClick: async () => {
                            try {
                              // إرسال SMS و Payment success email (بدون speech)
                              if (paymentResponse.data?.data?.orderId) {
                                await instance.post(`payall/pay/${code}/send-payment-notifications`, {
                                  orderId: paymentResponse.data.data.orderId,
                                });
                                console.log("✅ Payment notifications sent successfully");
                              }
                            } catch (notificationError) {
                              console.error("Error sending payment notifications:", notificationError);
                              toast.warning("Payment updated, but failed to send notifications");
                            }
                          },
                        },
                      ],
                    });
                  }
                } catch (speechError) {
                  // إذا لم يكن هناك Speech Subscription أو حدث خطأ، تجاهل الخطأ
                  console.log("ℹ️ No active speech subscription to update or error:", speechError.message);
                }
                
                fetchCustomerData();
                copyRenewalFromServer(paid.paymentExpiration);
              } else {
                console.error("خطأ في تحديث تاريخ الدفع:", paymentResponse.statusText);
                toast.error("❌ Error updating payment and subscription");
              }
            } catch (error) {
              console.error("Error updating SubscribeUntill:", error);
              toast.error(`❌ Error updating Subscribe ${name}`);
            }
          },
        },
        

      {
          label: "Both Monthly",
          onClick: async () => {
            try {
              const monthsToAdd = await new Promise((resolve) => {
                setTimeout(() => {
                  confirmAlert({
                  closeOnEscape: true,
                  closeOnClickOutside: true,
                  customUI: ({ onClose }) => {
                    let inputRef = null;
                    return (
                      <div className="react-confirm-alert-body">
                        <h1>Number of months</h1>
                        <p>Enter number of months to add</p>
                        <input
                          type="number"
                          min="1"
                          defaultValue="1"
                          ref={(el) => (inputRef = el)}
                          style={{ width: "100%", padding: "8px", boxSizing: "border-box" }}
                        />
                        <div className="react-confirm-alert-button-group">
                          <button
                            onClick={() => {
                              const val = parseInt(inputRef && inputRef.value, 10);
                              onClose();
                              resolve(val);
                            }}
                          >
                            OK
                          </button>
                          <button
                            onClick={() => {
                              onClose();
                              resolve(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  },
                  });
                }, 0);
              });
              if (!monthsToAdd || isNaN(monthsToAdd) || monthsToAdd <= 0) {
                toast.error("Invalid number of months");
                return;
              }

              const monthlyPreview = await fetchPayPreview(instance, code, monthsToAdd);
              const monthlyMessage = buildPayDialogMessage({
                name,
                preview: monthlyPreview,
                gridPaymentDate: paymentDate,
                includeAdminNotes: false,
              });
              const confirmedMonths = await new Promise((resolve) => {
                confirmAlert({
                  title: "Confirm months",
                  message: (
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      {`${monthlyMessage}\n\nAdd ${monthsToAdd} month(s). After Both Monthly: ${formatDate(monthlyPreview.previewPaymentDate)}`}
                    </span>
                  ),
                  buttons: [
                    { label: "OK", onClick: () => resolve(true) },
                    { label: "Cancel", onClick: () => resolve(false) },
                  ],
                });
              });
              if (!confirmedMonths) {
                return;
              }

              const paymentResponse = await instance.put(`payall/pay/${code}`, {
                NumberMonths: monthsToAdd,
              });
        
              if (paymentResponse.status === 200) {
                const paid = datesFromPayResponse(paymentResponse);
                toast.success(
                  buildPaySuccessToast({
                    writtenExpiry: paid.paymentExpiration,
                    previewExpiry: monthlyPreview.previewPaymentDate,
                    orderId: paid.orderId,
                  })
                );
                
                try {
                  const speechResponse = await instance.get(`speech-subscriptions/by-code/${code}`);
                  
                  if (speechResponse.status === 200 && speechResponse.data?.data?.IsActive) {
                    confirmAlert({
                      title: "Update Speech Subscription?",
                      message: "Do you want to update the Speech Subscription dates as well?",
                      buttons: [
                        {
                          label: "Yes",
                          onClick: async () => {
                            try {
                              await updateSpeechFromServerDates({
                                currentPaymentDate: paid.currentPaymentDate,
                                paymentExpiration: paid.paymentExpiration,
                                orderId: paid.orderId,
                                addSpeechAmount: true,
                              });
                              toast.success("Payment and Speech Subscription updated successfully");
                            } catch (error) {
                              console.error("Error updating Speech Subscription:", error);
                              toast.error("Payment updated, but failed to update Speech Subscription");
                            }
                          },
                        },
                        {
                          label: "No",
                          onClick: async () => {
                            try {
                              // إرسال SMS و Payment success email (بدون speech)
                              if (paymentResponse.data?.data?.orderId) {
                                await instance.post(`payall/pay/${code}/send-payment-notifications`, {
                                  orderId: paymentResponse.data.data.orderId,
                                });
                                console.log("✅ Payment notifications sent successfully");
                              }
                            } catch (notificationError) {
                              console.error("Error sending payment notifications:", notificationError);
                              toast.warning("Payment updated, but failed to send notifications");
                            }
                          },
                        },
                      ],
                    });
                  }
                } catch (speechError) {
                  // إذا لم يكن هناك Speech Subscription أو حدث خطأ، تجاهل الخطأ
                  console.log("ℹ️ No active speech subscription to update or error:", speechError.message);
                }
                
                fetchCustomerData();
                copyRenewalFromServer(paid.paymentExpiration);
              } else {
                console.error("خطأ في تحديث تاريخ الدفع:", paymentResponse.statusText);
                toast.error("❌ Error updating payment and subscription");
              }
            } catch (error) {
              console.error("Error updating SubscribeUntill:", error);
              toast.error(`❌ Error updating Subscribe ${name}`);
            }
          },
        },

        {
          label: "info",
          onClick: async () => {
            try {
              const [response, livePreview] = await Promise.all([
                instance.get(`Out`),
                fetchPayPreview(instance, code, 1),
              ]);
              if (response.status === 200) {
                const results = response.data.data; // الوصول إلى مصفوفة البيانات
                const dbResult = results.find(
                  (result) =>
                    result.databaseName.toLowerCase() === name.toLowerCase()
                );

                if (dbResult && dbResult.data.length > 0) {
                  const subscriptionUntil = formatDate(
                    dbResult.data[0].SubscribeUntill
                  );
                  const subscriptionTypeValue =
                    dbResult.data[0].SubscribtionType;

                  // تحديد نوع الباقة بناءً على القيمة
                  let subscriptionType;
                  if (subscriptionTypeValue === 0) {
                    subscriptionType = "Bronze";
                  } else if (subscriptionTypeValue === 1) {
                    subscriptionType = "Silver";
                  } else if (subscriptionTypeValue === 2) {
                    subscriptionType = "Golden";
                  } else if (subscriptionTypeValue === 3) {
                    subscriptionType = "Platinum";
                  } else if (subscriptionTypeValue === 4) {
                    subscriptionType = "Diamond";
                  }

                  const catalogUntil = formatDate(livePreview.currentPaymentDate);
                  const message = `Subscription for ${name}:
Catalog PaymentDate (live): ${catalogUntil}
Tenant SubscribeUntill: ${subscriptionUntil}
Type: ${subscriptionType}`;

                  confirmAlert({
                    title: "Subscription Information",
                    message: message,
                    buttons: [
                      {
                        label: "Copy",
                        onClick: async () => {
                          let liveExpiry;
                          try {
                            liveExpiry = liveExpiryFromPreview(
                              await fetchPayPreview(instance, code, 1)
                            );
                          } catch (previewError) {
                            toast.error("Could not load the live expiry. Refresh and try again.");
                            return;
                          }
                          const customer = customers.find((c) => c.Code === code);
                          const message = buildInfoCopyCardText({
                            name,
                            priceType,
                            storedExpiry: liveExpiry,
                            price: customer && customer.Price,
                            capitalizeName,
                          });

              
                          const copyToClipboard = () => {
                            if (navigator.clipboard) {
                              // Clipboard API is supported
                              navigator.clipboard
                                .writeText(message)
                                .then(() => toast.success("Message copied to clipboard"))
                                .catch(() => toast.error("Failed to copy message"));
                            } else {
                              // Fallback for older browsers or environments
                              const textarea = document.createElement("textarea");
                              textarea.value = message;
                              document.body.appendChild(textarea);
                              textarea.select();
                              try {
                                document.execCommand("copy");
                                toast.success("Message copied to clipboard");
                              } catch (err) {
                                toast.error("Failed to copy message");
                              }
                              document.body.removeChild(textarea);
                            }
                          };
              
                          // Copy the message to clipboard
                          copyToClipboard();
                        },
                      },
                                             {
          label: "WhatsApp",
          onClick: async () => {
            let liveExpiry;
            try {
              liveExpiry = liveExpiryFromPreview(
                await fetchPayPreview(instance, code, 1)
              );
            } catch (previewError) {
              toast.error("Could not load the live expiry. Refresh and try again.");
              return;
            }
            const customer = customers.find((c) => c.Code === code);

            const countryCode = "2";
            const fullPhoneNumber = `${countryCode}${customer.Phone}`;
            const message = buildRenewalClipboardText({
              name,
              priceType,
              paymentExpiration: liveExpiry,
              capitalizeName,
            });

            console.log(fullPhoneNumber);
            const isMobile = /iPhone|iPad|iPod|Android/i.test(
              navigator.userAgent
            );
            const url = isMobile
              ? `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(
                  message
                )}`
              : `https://web.whatsapp.com/send?phone=${fullPhoneNumber}&text=${encodeURIComponent(
                  message
                )}`;

            window.open(url, "_blank");
          },
        },

       

        {
          label: "SMS",
          onClick: async () => {
            let liveExpiry;
            try {
              liveExpiry = liveExpiryFromPreview(
                await fetchPayPreview(instance, code, 1)
              );
            } catch (previewError) {
              toast.error("Could not load the live expiry. Refresh and try again.");
              return;
            }
            const smsMessage = buildSmsRenewalText({
              name,
              priceType,
              storedExpiry: liveExpiry,
              capitalizeName,
            });
            console.log("Generated message:", smsMessage);

            try {
              const res = await instance.post("sms/send-sms", {
                code: code,
                message: smsMessage,
              });

              if (res.status === 200) {
                toast.success("SMS sent successfully");
              } else {
                console.error("Error sending SMS:", res.statusText);
                toast.error("Error sending SMS");
              }
            } catch (err) {
              console.error("Error sending SMS:", err);
              toast.error("Error sending SMS");
            }
          },
        },

                      
                      {
                        label: "notice",
                        onClick: async () => {
                          let liveExpiry;
                          try {
                            liveExpiry = liveExpiryFromPreview(
                              await fetchPayPreview(instance, code, 1)
                            );
                          } catch (previewError) {
                            toast.error("Could not load the live expiry. Refresh and try again.");
                            return;
                          }
                          const customer = customers.find((c) => c.Code === code);
              
                          const countryCode = "2";
                          const fullPhoneNumber = `${countryCode}${customer.Phone}`;
                          const message = buildNoticeText({
                            name,
                            priceType,
                            storedExpiry: liveExpiry,
                            capitalizeName,
                          });
              
                          console.log(fullPhoneNumber);
                          const isMobile = /iPhone|iPad|iPod|Android/i.test(
                            navigator.userAgent
                          );
                          const url = isMobile
                            ? `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(
                                message
                              )}`
                            : `https://web.whatsapp.com/send?phone=${fullPhoneNumber}&text=${encodeURIComponent(
                                message
                              )}`;
              
                          window.open(url, "_blank");
                        },
                      },

                      {
                        label: "Late notice",
    
                          onClick: async () => {
                            let liveExpiry;
                            try {
                              liveExpiry = liveExpiryFromPreview(
                                await fetchPayPreview(instance, code, 1)
                              );
                            } catch (previewError) {
                              toast.error("Could not load the live expiry. Refresh and try again.");
                              return;
                            }
                            const customer = customers.find((c) => c.Code === code);
                
                            const countryCode = "2";
                            const fullPhoneNumber = `${countryCode}${customer.Phone}`;
                            const message = buildLateNoticeText({
                              name,
                              priceType,
                              storedExpiry: liveExpiry,
                              capitalizeName,
                            });
                      
                          console.log(fullPhoneNumber);
                          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                          const url = isMobile
                            ? `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(message)}`
                            : `https://web.whatsapp.com/send?phone=${fullPhoneNumber}&text=${encodeURIComponent(message)}`;
                      
                          window.open(url, "_blank");
                        },
                      },

                     
                      {
                        label: "Late",
    
                          onClick: async () => {
                            let liveExpiry;
                            try {
                              liveExpiry = liveExpiryFromPreview(
                                await fetchPayPreview(instance, code, 1)
                              );
                            } catch (previewError) {
                              toast.error("Could not load the live expiry. Refresh and try again.");
                              return;
                            }
                            const customer = customers.find((c) => c.Code === code);
                
                            const countryCode = "2";
                            const fullPhoneNumber = `${countryCode}${customer.Phone}`;
                            const message = buildLateText({
                              name,
                              storedExpiry: liveExpiry,
                              capitalizeName,
                            });
                      
                          console.log(fullPhoneNumber);
                          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                          const url = isMobile
                            ? `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(message)}`
                            : `https://web.whatsapp.com/send?phone=${fullPhoneNumber}&text=${encodeURIComponent(message)}`;
                      
                          window.open(url, "_blank");
                        },
                      },
                      
                      {
                        label: "OK",
                        onClick: () => {},
                      },
                    ],
                  });
                } else {
                  console.error(`No data found for database ${name}`);
                  toast.error(`No data found for database ${name}`);
                }
              } else {
                console.error("Failed to fetch data:", response.statusText);
                toast.error("Failed to fetch data");
              }
            } catch (error) {
              console.error("Error fetching subscription data:", error);
              toast.error("Error fetching subscription data");
            }
          },
        },

        {
          label: "Copy",
          onClick: async () => {
            let liveExpiry;
            try {
              liveExpiry = liveExpiryFromPreview(
                await fetchPayPreview(instance, code, 1)
              );
            } catch (previewError) {
              toast.error("Could not load the live expiry. Refresh and try again.");
              return;
            }
            const message = buildRenewalClipboardText({
              name,
              priceType,
              paymentExpiration: liveExpiry,
              capitalizeName,
            });
            const copyToClipboard = () => {
              if (navigator.clipboard) {
                // Clipboard API is supported
                navigator.clipboard
                  .writeText(message)
                  .then(() => toast.success("Message copied to clipboard"))
                  .catch(() => toast.error("Failed to copy message"));
              } else {
                // Fallback for older browsers or environments
                const textarea = document.createElement("textarea");
                textarea.value = message;
                document.body.appendChild(textarea);
                textarea.select();
                try {
                  document.execCommand("copy");
                  toast.success("Message copied to clipboard");
                } catch (err) {
                  toast.error("Failed to copy message");
                }
                document.body.removeChild(textarea);
              }
            };

            // Copy the message to clipboard
            copyToClipboard();
          },
        },
       

        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  };

  const calculateTotals = () => {
    const filteredCustomers = customers.filter(filterCustomers);
    const total = filteredCustomers.reduce(
      (sum, customer) => sum + (parseFloat(customer.Price) || 0),
      0
    );
    setTotalPrice(Math.floor(total));
    setTotalRows(filteredCustomers.length);
  };

  const formatDateRange = (dateFilter) => {
    if (dateFilter instanceof Date) {
      return dateFilter.toLocaleDateString();
    } else if (dateFilter && dateFilter.start && dateFilter.end) {
      return `${dateFilter.start.toLocaleDateString()} - ${dateFilter.end.toLocaleDateString()}`;
    }
    return "";
  };

  const [userInfo, setUserInfo] = useState({
    username: "",
    role: "",
    lastLoginTime: "",
  });

  useEffect(() => {
    const username = localStorage.getItem("username");
    const role = localStorage.getItem("role");
    const lastLoginTime = localStorage.getItem("lastLoginTime");

    if (username && role && lastLoginTime) {
      setUserInfo({
        username,
        role,
        lastLoginTime,
      });
    }
  }, []);

  const formatDate3 = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    return date.toLocaleDateString("en-GB", options);
  };

  return (
    <div>
      <header>
        <h1>OwlTech</h1>
        <div id="user-info">
          <span id="username"> {userInfo.username}</span> |
          <span id="role">{userInfo.role}</span> |
          <span id="last-login">{formatDate3(userInfo.lastLoginTime)}</span>
        </div>
      </header>
      {(userRole === "user" || userRole === "supervisor") && (
        <>
          <button onClick={handleLogout} className="button logout-button Red">
            Logout
          </button>
          <button
            className="button logout-button Red"
            onClick={() => (window.location.href = "/ChangePassword")}
          >
            ChangePassword
          </button>
          {userRole === "supervisor" && (
            <button
              onClick={() => (window.location.href = "/price")}
              className="button"
            >
              Price
            </button>
          )}
        </>
      )}

      {userRole === "admin" && (
        <>
          <CustomerFilters
            filters={filters}
            toggleFilter={toggleFilter}
            handleLogout={handleLogout}
            handleRegisterClick={handleRegisterClick}
            handleUsersClick={handleUsersClick}
            paymentNoticeCount={paymentNoticeCount}
            arrearsCount={arrearsCount}
            onlineCount={onlineCount}
            offlineCount={offlineCount}
          />

          <CustomerSearch
            searchTerm={searchTerm}
            excludeTerm={excludeTerm}
            datalistData={datalistData}
            handleSearch={handleSearch}
            handleExcludeSearch={handleExcludeSearch}
            resetSearch={resetSearch}
            dateFilter={dateFilter}
            formatDateRange={formatDateRange}
          />
        </>
      )}

      <CustomerTable
        customers={sortedCustomers}
        sortConfig={sortConfig}
        handleSort={handleSort}
        filterCustomers={filterCustomers}
        handleRowClick={(code, name) => handleRowClick(code, name, navigate)}
        handlePay={(code, paymentDate, name, priceType, phone) =>
          handlePay(code, paymentDate, name, priceType, phone)
        }
        formatDate={formatDate}
        userRole={userRole} // إضافة هذه الخاصية
      />

      {userRole === "admin" && (
        <footer
          className={isTotalVisible ? "open" : ""}
          style={{
            transition: "height 2s",
          }}
        >
          {isTotalVisible && (
            <div className="total">
              <h2>Rows: {totalRows}</h2>
              <h4>Total: {totalPrice}</h4>
              <h4>Speech Total: {Math.floor(speechTotal)}</h4>
            </div>
          )}
          <span
            className="copy"
            onClick={toggleTotalVisibility}
            aria-label="Toggle total visibility"
          >
            © 2025 OwlTech All rights reserved.
          </span>
        </footer>
      )}
      {(userRole === "user" || userRole === "supervisor") && (
        <footer>
          <span className="copy">© 2025 OwlTech All rights reserved.</span>
        </footer>
      )}
    </div>
  );
};

export default Customers;
